import { Component, input, OnDestroy, OnInit, output } from "@angular/core";
import { forkJoin, map, Observable, Subject, take, takeUntil } from "rxjs";

import { PayGroup } from "@shared/models/pay-groups";
import { FileManagementService } from "@shared/services/file-management/file-management.service";
import { ToastService } from "@shared/services/toast/toast.service";
import { Employee } from "@shared/models/employee.interface";
import { NetPaymentOrdersService } from "@shared/services/net-payment/net-payment-orders.service";
import { FileManagementUploadService } from "@shared/services/file-management/file-management-upload.service";
import { FileHistory, FileHistoryPagination } from "@shared/models/file-manager";

@Component({
	selector: "app-net-payment-actions",
	templateUrl: "./net-payment-actions.component.html",
	styleUrls: ["./net-payment-actions.component.scss"]
})
export class NetPaymentActionsComponent implements OnInit, OnDestroy {
	paygroupId = input<string>("");
	isPaymentOrderMenuOpen = false;
	payrollCycleId = "";
	payCycleExternalId = "";
	payCycleId = "";
	payGroup!: PayGroup;

	onEmployeeSearch = output<string>();
	refreshHandler = output<void>();
	goToFileHistoryHandler = output<void>();
	destroy$ = new Subject<void>();

	isEnabledCPNDeductionUpload = false;
	isEnabledG2NFileUpload = true;
	isPaymentDeductionEnabled = false;
	fileHistoryItems!: FileHistory[];

	constructor(
		private toastService: ToastService,
		private fileManagmentService: FileManagementService,
		private netPaymentOrdersService: NetPaymentOrdersService,
		private fileManagementUploadService: FileManagementUploadService
	) {}

	ngOnInit(): void {
		if (history.state) {
			this.payGroup = history.state.payGroup;
			this.payCycleId = history.state.payCycleId;
			this.payCycleExternalId = history.state.payCycleExternalId;
			this.payrollCycleId = history.state.payrollCycleId;

			/** FOR PAYMENT ORDER BUTTONS */
			this.isPaymentDeductionEnabled = history.state.payGroup?.data?.cpNowDeductionsEnabled;
			this.isEnabledCPNDeductionUpload = history.state.milestone?.status === "COMPLETE";

			if (this.isPaymentDeductionEnabled) {
				this.getPaymentOrderFiles().pipe(take(1)).subscribe();
			}
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	searchEmployeeFn = (query: string): Observable<Employee[]> => {
		return this.netPaymentOrdersService.getEmployeeAutocomplete(query, this.paygroupId());
	};

	displayEmployeeFn = (employee: Employee): string => {
		return employee ? `${employee.externalId} ${employee.data.firstName} ${employee.data.lastName}` : "";
	};

	refresh(): void {
		this.refreshHandler.emit();
		if (this.isPaymentDeductionEnabled) {
			this.getPaymentOrderFiles().pipe(take(1)).subscribe();
		}
	}

	goToFileHistory(): void {
		this.goToFileHistoryHandler.emit();
	}

	uploadG2NFile(files: any) {
		try {
			const file: File = files[0];
			const formData = new FormData();
			formData.append("file", file);
			formData.append("payGroupId", this.payGroup.id);
			formData.append("payCycleId", this.payCycleId);
			formData.append("fileType", "EXCEL");
			formData.append("sheetIndex", "0");
			formData.append("headerIndex", "4");

			if (file.name.includes(this.payrollCycleId)) {
				this.fileManagementUploadService.uploadG2NFile(formData);
			} else {
				if (file.name.includes(this.payCycleExternalId)) {
					this.fileManagementUploadService.uploadG2NFile(formData);
				} else {
					this.payrollCycleId
						? this.toastService.showWarning(
								`You have selected the wrong file for PayCycle: ${this.payrollCycleId}  or  ${this.payCycleExternalId}`
						  )
						: this.toastService.showWarning(
								`You have selected the wrong file for PayCycle: ${this.payCycleExternalId}`
						  );
				}
			}
		} catch (error) {
			this.toastService.showError("There was an error loading the file. Please try again");
		}
	}

	/**
	 * Uploads CPN deduction file
	 * @param files: CPN Deduction execel file uploaded
	 */
	uploadCPNDeductionFile(files: any): void {
		const file: File = files[0];
		const formData = new FormData();

		formData.append("file", file);
		formData.append("payCycleId", this.payCycleId);
		formData.append("fileType", "EXCEL");

		this.fileManagementUploadService.uploadCPNDeductionFile(formData);
		this.goToFileHistory();
	}

	/**
	 * @returns: G2N files, Deduction files and History items (If available)
	 */
	getPaymentOrderFiles(): Observable<FileHistory[]> {
		this.payGroup.id, this.payCycleId;
		return forkJoin([
			this.fileManagmentService.getFilesG2NFileHistoryByPaygroupIncludingPayCycleID(
				this.payGroup.id,
				this.payCycleId,
				-1,
				0
			),
			this.fileManagmentService.getCPNDeductionFilesHistoryByPaygroupIncludingPayCycleID(
				this.payGroup.id,
				this.payCycleId,
				-1,
				0
			)
		]).pipe(
			takeUntil(this.destroy$),
			map((data: [FileHistoryPagination, FileHistoryPagination]) => {
				const [fileHistory, deductionFile] = data;

				const fileItems: FileHistory[] = fileHistory.items;

				/** Add deduction file items if available */
				if (deductionFile.items.length) {
					deductionFile.items.forEach(item => {
						fileItems.push(item);
					});
				}

				/**  Add history items if available */
				if (history.state.milestone.resetHistory.length > 0) {
					for (let i = 0; i < history.state.milestone.resetHistory.length; i++) {
						fileItems.push({
							userReset: history.state.milestone.resetHistory[i].userId,
							createdAt: history.state.milestone.resetHistory[i].resetAt
						});
					}
				}

				const fileResults = fileItems.sort(
					(a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
				);

				this.checkPaymentOrderUploadButtonStatus(fileResults);

				return fileResults;
			})
		);
	}

	/**
	 * @param fileItems: G2N files, Deduction files and History items (If available)
	 * @returns: Enabling/disabling of Payment Order upload buttons
	 */
	checkPaymentOrderUploadButtonStatus(fileItems: FileHistory[]): void {
		if (fileItems.length === 0) return;

		const fileItem = fileItems[0];

		if (!fileItem.hasOwnProperty("userReset")) {
			if (fileItem.fileType === "G2N") {
				this.isEnabledCPNDeductionUpload =
					(fileItem.status === "SUCCESS" &&
						fileItem.attributes?.NET_HANDSHAKE?.status === "COMPLETED" &&
						fileItem.attributes?.NET_HANDSHAKE?.allMatch) ||
					fileItem.taskOverridingStatus?.status === "COMPLETED";
			} else if (fileItem.hasOwnProperty("userCancelled")) {
				this.isEnabledG2NFileUpload = true;
			} else if (fileItem.fileType === "DEDUCTIONS") {
				this.isEnabledG2NFileUpload = true;
			} else {
				this.isEnabledG2NFileUpload = false;
				this.isEnabledCPNDeductionUpload = false;
			}
		} else {
			this.isEnabledCPNDeductionUpload = false;
		}
	}

	runPayrollIntegration(): void {
		this.fileManagmentService
			.refreshPayrollIntegrationNets(this.payCycleId)
			.pipe(take(1))
			.subscribe({
				next: _ => this.toastService.showSuccess("Request to trigger payroll integration was sent sucessfully"),
				error: _ =>
					this.toastService.showError(
						"An error has occured while trying to send a request to trigger the payroll integration"
					)
			});
		this.refreshHandler.emit();
	}

	onEmployeeSelected(employee: Employee): void {
		this.onEmployeeSearch.emit(employee.id ?? "");
	}
}
